import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ArrowRightToLine,
  ChevronDown,
  Plus,
  CalendarDays,
  FileCheck,
  ArrowDown,
  X,
} from "lucide-react";
import AllWorkers from "./AllWorkers";
import WorkerAttendance from "./WorkerAttendance";
import UnifiedForm from "./NewWorker";
import WorkerPayment from "./WorkerPayment";
import { BASE_URL } from "utils/constant";
import profileIcon from "../../../assets/contractor_icon.png";
import { MdOutlineGroupAdd } from "react-icons/md";
import { FaUser } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import PermissionEmptyState from "components/UI_components/EmptyStateFinance";
import { useAlert } from "components/UI_components/Alert";

function QuickMarkAttendanceModal({
  isOpen,
  onClose,
  worker,
  onUpdate,
  dayTypes,
  initialOvertimeHours,
  initialOvertimeWages,
  attendanceStatus,
}) {
  const [status, setStatus] = useState(attendanceStatus || "Unmarked");
  const [dayType, setDayType] = useState(dayTypes);
  const [overtimeHours, setOvertimeHours] = useState(initialOvertimeHours);
  const [overtimeWages, setOvertimeWages] = useState(initialOvertimeWages);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setStatus(attendanceStatus || "Unmarked");
      setDayType(dayTypes || null);
      setOvertimeHours(initialOvertimeHours || 0);
      setOvertimeWages(initialOvertimeWages || 0);
    }

  }, [isOpen, attendanceStatus]);

  useEffect(() => {
    // reset overtime fields when switching to half or non-present
    if (status !== "Present") {
      setDayType("full");
      setOvertimeHours(initialOvertimeHours || 0);
      setOvertimeWages(initialOvertimeWages || 0);
    }
  }, [status, initialOvertimeHours, initialOvertimeWages]);

  if (!isOpen) return null;

  const handleUpdateAttendance = async () => {
    setLoading(true);
    setError("");

    // validation
    if (status === "Present" && !["half", "full"].includes(dayType)) {
      setError("Please select Half Day or Full Day.");
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem("access");

      const payload = {
        attendance_status: status,
        persion_count: 1,
        half_day: status === "Present" ? dayType === "half" : false,
        full_day: status === "Present" ? dayType === "full" : false,
        overtime_hours:
          status === "Present" && dayType === "full"
            ? Number(overtimeHours) || 0
            : 0,
        overtime_wage:
          status === "Present" && dayType === "full"
            ? Number(overtimeWages) || 0
            : 0,
      };

      const response = await fetch(
        `${BASE_URL}/worker/mark-worker-attendance/${worker.worker_id}/`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Failed to update attendance");
      }

      onUpdate();
      onClose();
    } catch (err) {
      console.error(err);
      setError("Failed to update attendance. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-md">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-lg font-semibold">Mark Attendance</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mr-3">
              <FaUser />
            </div>
            <div>
              <h3 className="font-semibold">{worker?.worker_name}</h3>
              <p className="text-sm text-gray-500">{worker?.worker_workname}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button
              className={`py-2 px-4 rounded-lg flex items-center justify-center ${
                status === "Present"
                  ? "bg-[#E8F4E9] text-black"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              onClick={() => setStatus("Present")}
            >
              Present
            </button>

            <button
              className={`py-2 px-4 rounded-lg flex items-center justify-center ${
                status === "Absent"
                  ? "bg-[#F9E8E8] text-black"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              onClick={() => setStatus("Absent")}
            >
              Absent
            </button>
          </div>

          {/* When Present -> choose Half or Full */}
          {status === "Present" && (
            <div className="space-y-3">
              <div className="text-sm font-medium">Select day type</div>
              <div className="flex gap-2">
                <label
                  className={`flex-1 py-2 px-4 rounded-lg text-center cursor-pointer border ${
                    dayType === "half"
                      ? "bg-[#FFF7E6]"
                      : "bg-gray-100 hover:bg-gray-200"
                  }`}
                >
                  <input
                    type="radio"
                    name="dayType"
                    value="half"
                    className="hidden"
                    checked={dayType === "half"}
                    onChange={() => setDayType("half")}
                  />
                  Half Day
                </label>

                <label
                  className={`flex-1 py-2 px-4 rounded-lg text-center cursor-pointer border ${
                    dayType === "full"
                      ? "bg-[#E8F4E9]"
                      : "bg-gray-100 hover:bg-gray-200"
                  }`}
                >
                  <input
                    type="radio"
                    name="dayType"
                    value="full"
                    className="hidden"
                    checked={dayType === "full"}
                    onChange={() => setDayType("full")}
                  />
                  Full Day
                </label>
              </div>

              {/* Overtime only visible for Full Day */}
              {dayType === "full" && (
                <div className="grid grid-cols-2 gap-2 items-end">
                  <div>
                    <label className="text-sm block mb-1">Overtime hours</label>
                    <input
                      type="number"
                      min={0}
                      step="0.5"
                      value={overtimeHours}
                      onChange={(e) => setOvertimeHours(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="text-sm block mb-1">Overtime wages</label>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={overtimeWages}
                      onChange={(e) => setOvertimeWages(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdateAttendance}
              disabled={loading}
              className="px-4 py-2 bg-text-clr text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {loading ? "Updating..." : "Update"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const WorkerDashboard = ({ userPermissions, permissionsLoading }) => {
  const [topTab, setTopTab] = useState("Dashboard");
  const [dateRange, setDateRange] = useState("Daily");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isAttendanceOpen, setIsAttendanceOpen] = useState(false);
  const [isNewWorkerOpen, setIsNewWorkerOpen] = useState(false);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [quickMarkModal, setQuickMarkModal] = useState({
    open: false,
    worker: null,
    attendanceStatus: null
  });

  const [allAttendance, setAllAttendance] = useState([]);
  const [filteredWorkers, setFilteredWorkers] = useState([]);
  const [workerRates, setWorkerRates] = useState({});
  const [workerAttendanceCounts, setWorkerAttendanceCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [sortBy, setSortBy] = useState("name_asc");
  const sortRef = useRef(null);
  const navigate = useNavigate();
  const { addAlert } = useAlert();
  const siteId =
    typeof window !== "undefined" ? localStorage.getItem("site") : null;

  // Helper functions for date manipulation
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getWeekRange = (date) => {
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(date.setDate(diff));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { start: monday, end: sunday };
  };

  const getMonthRange = (date) => {
    const start = new Date(date.getFullYear(), date.getMonth(), 1);
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    return { start, end };
  };

  const getDaysInRange = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (dateRange === "Daily") {
      return 1;
    } else if (dateRange === "Weekly") {
      const { start, end } = getWeekRange(new Date(currentDate));
      const rangeEnd = end > today ? today : end;
      return Math.ceil((rangeEnd - start) / (1000 * 60 * 60 * 24)) + 1;
    } else {
      const { start, end } = getMonthRange(new Date(currentDate));
      const rangeEnd = end > today ? today : end;
      return Math.ceil((rangeEnd - start) / (1000 * 60 * 60 * 24)) + 1;
    }
  };

  // Navigation functions
  const canGoNext = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (dateRange === "Daily") {
      const nextDay = new Date(currentDate);
      nextDay.setDate(currentDate.getDate() + 1);
      nextDay.setHours(0, 0, 0, 0);
      return nextDay <= today;
    } else if (dateRange === "Weekly") {
      const { end } = getWeekRange(new Date(currentDate));
      const nextWeek = new Date(end);
      nextWeek.setDate(end.getDate() + 1);
      return nextWeek <= today;
    } else {
      const { end } = getMonthRange(new Date(currentDate));
      const nextMonth = new Date(end);
      nextMonth.setDate(end.getDate() + 1);
      return nextMonth <= today;
    }
  };

  const handlePrevious = () => {
    const newDate = new Date(currentDate);
    if (dateRange === "Daily") {
      newDate.setDate(currentDate.getDate() - 1);
    } else if (dateRange === "Weekly") {
      newDate.setDate(currentDate.getDate() - 7);
    } else {
      newDate.setMonth(currentDate.getMonth() - 1);
    }
    setCurrentDate(newDate);
  };

  const handleNext = () => {
    if (!canGoNext()) return;

    const newDate = new Date(currentDate);
    if (dateRange === "Daily") {
      newDate.setDate(currentDate.getDate() + 1);
    } else if (dateRange === "Weekly") {
      newDate.setDate(currentDate.getDate() + 7);
    } else {
      newDate.setMonth(currentDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  // Fetch all attendance
  const fetchAllAttendance = async () => {
    const accessToken = localStorage.getItem("access");
    const site = localStorage.getItem("site");

    try {
      const response = await fetch(
        `${BASE_URL}/worker/list-all-attendance/?site_id=${site}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      const data = await response.json();
      if (data?.status && Array.isArray(data?.data)) {
        setAllAttendance(data.data);
      } else {
        setAllAttendance([]);
      }
    } catch (e) {
      console.error("Error fetching attendance:", e);
      setAllAttendance([]);
    }
  };

  // Filter workers based on date range
  const filterWorkersByDateRange = () => {
    if (!allAttendance.length) {
      setFilteredWorkers([]);
      return;
    }

    let startDate, endDate;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (dateRange === "Daily") {
      startDate = formatDate(currentDate);
      endDate = startDate;
    } else if (dateRange === "Weekly") {
      const { start, end } = getWeekRange(new Date(currentDate));
      const rangeEnd = end > today ? today : end;
      startDate = formatDate(start);
      endDate = formatDate(rangeEnd);
    } else {
      const { start, end } = getMonthRange(new Date(currentDate));
      const rangeEnd = end > today ? today : end;
      startDate = formatDate(start);
      endDate = formatDate(rangeEnd);
    }

    // Filter attendance records in date range
    const filteredAttendance = allAttendance.filter((record) => {
      const recordDate = record.attendance_date;
      return recordDate >= startDate && recordDate <= endDate;
    });

    // Get unique workers with their latest attendance status in the range
    const workerMap = new Map();

    filteredAttendance.forEach((record) => {
      const workerId = record.worker_id;
      if (!workerMap.has(workerId)) {
        workerMap.set(workerId, {
          worker_id: workerId,
          worker_name: record.worker_name,
          worker_workname: record.worker_workname,
          worker_workcategory: record.worker_workcategory,
          attendance_status: record.attendance,
          records: [],
        });
      }
      workerMap.get(workerId).records.push(record);
    });

    // Calculate attendance for each worker
    const workersWithStats = Array.from(workerMap.values()).map((worker) => {
      const presentCount = worker.records.filter(
        (r) => r.attendance.toLowerCase() === "present"
      ).length;
      const absentCount = worker.records.filter(
        (r) => r.attendance.toLowerCase() === "absent"
      ).length;

      // Find the latest record for `last_updated_by`
      const latestRecord = worker.records.reduce((latest, r) => {
        if (!latest) return r;
        const latestTime = latest.last_updated_on || "";
        const currentTime = r.last_updated_on || "";
        return currentTime > latestTime ? r : latest;
      }, null);

      return {
        ...worker,
        presentDays: presentCount,
        absentDays: absentCount,
        totalDays: worker.records.length,
        last_updated_by: latestRecord ? latestRecord.last_updated_by : null,
      };
    });

    setFilteredWorkers(workersWithStats);
  };

  // Fetch worker rates
  const fetchWorkerRates = async () => {
    if (!filteredWorkers || filteredWorkers.length === 0) return;

    const accessToken = localStorage.getItem("access");
    try {
      const results = await Promise.all(
        filteredWorkers.map(async (w) => {
          try {
            const res = await fetch(
              `${BASE_URL}/worker/get-workerpayment-of-worker/`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({ worker_id: w.worker_id }),
              }
            );
            const data = await res.json();
            if (
              data?.status &&
              Array.isArray(data.data) &&
              data.data.length > 0
            ) {
              const record = data.data[0];
              return [w.worker_id, record.amount];
            }
          } catch (_) {}
          return [w.worker_id, null];
        })
      );

      const map = {};
      results.forEach(([id, amt]) => {
        map[id] = amt;
      });
      setWorkerRates(map);
    } catch (_) {}
  };

  // Calculate summary stats
  const calculateSummaryStats = () => {
    if (!filteredWorkers.length) {
      return {
        workersPresent: 0,
        totalWorkingDays: getDaysInRange(),
        attendanceRate: 0,
        absenteeismRate: 0,
      };
    }

    const totalWorkingDays = getDaysInRange();
    const totalPossibleAttendance = filteredWorkers.length * totalWorkingDays;
    const totalPresent = filteredWorkers.reduce(
      (sum, w) => sum + w.presentDays,
      0
    );

    // Count workers who were present at least once in the range
    const workersPresent = filteredWorkers.filter(
      (w) => w.presentDays > 0
    ).length;

    const attendanceRate =
      totalPossibleAttendance > 0
        ? (totalPresent / totalPossibleAttendance) * 100
        : 0;
    const absenteeismRate = 100 - attendanceRate;

    return {
      workersPresent,
      totalWorkingDays,
      attendanceRate,
      absenteeismRate,
    };
  };

  const stats = calculateSummaryStats();

  const summaryCards = [
    { label: "Workers Present", value: String(stats.workersPresent) },
    { label: "Total Working Days", value: String(stats.totalWorkingDays) },
    { label: "Attendance %", value: `${stats.attendanceRate.toFixed(2)}%` },
    {
      label: "Absenteeism Rate",
      value: `${stats.absenteeismRate.toFixed(2)}%`,
    },
  ];

  // Sort dropdown close on outside click
  useEffect(() => {
    const onClickOutside = (e) => {
      if (isSortOpen && sortRef.current && !sortRef.current.contains(e.target)) {
        setIsSortOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [isSortOpen]);

  // Sorted workers based on selection
  const sortedWorkers = useMemo(() => {
    const arr = [...filteredWorkers];
    const totalDaysInRange = getDaysInRange();
    const getAttendancePercent = (w) => {
      const present = w.presentDays || 0;
      return totalDaysInRange > 0 ? (present / totalDaysInRange) * 100 : 0;
    };
    const getTotalPay = (w) => {
      const rate = workerRates[w.worker_id];
      const present = w.presentDays || 0;
      if (rate == null) return null;
      return rate * present;
    };

    const compareNumberWithNullLast = (a, b, dir = "desc") => {
      const aNull = a == null;
      const bNull = b == null;
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      return dir === "desc" ? b - a : a - b;
    };

    switch (sortBy) {
      case "name_asc":
        arr.sort((a, b) => (a.worker_name || "").localeCompare(b.worker_name || ""));
        break;
      case "name_desc":
        arr.sort((a, b) => (b.worker_name || "").localeCompare(a.worker_name || ""));
        break;
      case "attendance_desc":
        arr.sort((a, b) => getAttendancePercent(b) - getAttendancePercent(a));
        break;
      case "attendance_asc":
        arr.sort((a, b) => getAttendancePercent(a) - getAttendancePercent(b));
        break;
      case "pay_desc":
        arr.sort((a, b) => compareNumberWithNullLast(getTotalPay(a), getTotalPay(b), "desc"));
        break;
      case "pay_asc":
        arr.sort((a, b) => compareNumberWithNullLast(getTotalPay(a), getTotalPay(b), "asc"));
        break;
      default:
        break;
    }
    return arr;
  }, [filteredWorkers, workerRates, sortBy, dateRange, currentDate]);

  // Effects
  useEffect(() => {
    fetchAllAttendance();
  }, []);

  useEffect(() => {
    filterWorkersByDateRange();
  }, [allAttendance, currentDate, dateRange]);

  useEffect(() => {
    fetchWorkerRates();
  }, [filteredWorkers]);

  // Format display date
  const getDisplayDate = () => {
    if (dateRange === "Daily") {
      const day = currentDate.getDate();
      const month = currentDate.toLocaleString(undefined, { month: "long" });
      const year = currentDate.getFullYear();
      const isToday = formatDate(currentDate) === formatDate(new Date());
      return isToday
        ? `Today, ${day} ${month}, ${year}`
        : `${day} ${month}, ${year}`;
    } else if (dateRange === "Weekly") {
      const { start, end } = getWeekRange(new Date(currentDate));
      const today = new Date();
      const rangeEnd = end > today ? today : end;
      return `${start.getDate()} ${start.toLocaleString(undefined, {
        month: "short",
      })} - ${rangeEnd.getDate()} ${rangeEnd.toLocaleString(undefined, {
        month: "short",
      })}, ${rangeEnd.getFullYear()}`;
    } else {
      const month = currentDate.toLocaleString(undefined, { month: "long" });
      const year = currentDate.getFullYear();
      return `${month}, ${year}`;
    }
  };

  const handleAttendanceClick = (e, worker) => {
    e.stopPropagation();

    if (
      userPermissions?.edit_worker_management ||
      userPermissions?.is_admin ||
      userPermissions?.joined_as === "owner"
    ) {
      setQuickMarkModal({
        open: true,
        worker,
        attendanceStatus: worker?.attendance_status
      });
    } else {
      addAlert(
        "You must have edit worker management permission to mark worker attendance.",
        "error"
      );
    }
  };

  if (
    !userPermissions.is_admin &&
    !userPermissions.view_worker_management &&
    !permissionsLoading
  ) {
    return (
      <PermissionEmptyState
        module="Worker Management"
        description="You don't have permission to access the worker management for this site. Only the administrator can access this feature."
      />
    );
  }

  const isToday = formatDate(currentDate) === formatDate(new Date());

  return (
    <div className="bg-gray-100 w-full min-h-screen relative">
      <div className="p-8">
        <div className="mx-auto max-w-7xl">
          {isPaymentOpen ? (
            <WorkerPayment onClose={() => setIsPaymentOpen(false)} />
          ) : (
            <>
              {/* Top tabs */}
              <div className="flex items-center gap-8 -mt-2 mb-4 text-sm">
                <button
                  onClick={() => setTopTab("Dashboard")}
                  className={`font-semibold pb-1 ${
                    topTab === "Dashboard"
                      ? "text-[#3B0069] border-b-2 border-[#3B0069]"
                      : "text-[#8F8F8F]"
                  }`}
                >
                  Dashboard
                </button>
                <button
                  onClick={() => setTopTab("All Workers")}
                  className={`font-semibold pb-1 ${
                    topTab === "All Workers"
                      ? "text-[#3B0069] border-b-2 border-[#3B0069]"
                      : "text-[#8F8F8F]"
                  }`}
                >
                  All Workers
                </button>
              </div>

              {topTab === "All Workers" ? (
                <AllWorkers
                  onOpenPayment={() => setIsPaymentOpen(true)}
                  onOpenAttendance={() => setIsAttendanceOpen(true)}
                  onOpenNewWorker={() => setIsNewWorkerOpen(true)}
                  userPermissions={userPermissions}
                />
              ) : (
                <>
                  {/* Summary cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    {summaryCards.map((card) => (
                      <div
                        key={card.label}
                        className="bg-white rounded-xl shadow-sm px-2 py-3 text-center"
                      >
                        <div className="text-3xl font-semibold">
                          {card.value}
                        </div>
                        <div className="text-[14px] text-gray-500 mt-2">
                          {card.label}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Date navigation and actions */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="inline-flex items-center bg-white rounded-xl px-4 py-2 shadow-sm border-2 border-[#3B0069]">
                      <button
                        onClick={handlePrevious}
                        className="mr-2 hover:bg-gray-100 rounded p-1"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <CalendarDays className="w-4 h-4 mr-2" />
                      <span className="text-sm">{getDisplayDate()}</span>
                      <button
                        onClick={handleNext}
                        disabled={!canGoNext()}
                        className={`ml-2 hover:bg-gray-100 rounded p-1 ${
                          !canGoNext() ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex items-center gap-3">
                      <select
                        value={dateRange}
                        onChange={(e) => {
                          setDateRange(e.target.value);
                          setCurrentDate(new Date());
                        }}
                        className="px-3 py-2 bg-[#ECE6F0] text-[#3B0069] border-1 border-[#3B0069] rounded-xl font-normal text-sm"
                      >
                        <option value="Daily">Daily</option>
                        <option value="Weekly">Weekly</option>
                        <option value="Monthly">Monthly</option>
                      </select>
                      <button
                        onClick={() => {
                          if (
                            userPermissions?.edit_worker_management ||
                            userPermissions?.is_admin ||
                            userPermissions?.joined_as === "owner"
                          ) {
                            setIsAttendanceOpen(true);
                          } else {
                            addAlert(
                              "You must have edit worker management permission to mark worker attendance.",
                              "error"
                            );
                          }
                        }}
                        className="px-3 py-2 bg-[#ECE6F0] text-[#3B0069] border-1 border-[#3B0069] rounded-xl font-normal text-sm flex items-center gap-2"
                      >
                        <FileCheck className="w-5 h-5" />
                        Mark Attendance
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-between mb-3">
                    <div className="flex items-center justify-start">
                      <p className="text-sm text-gray-500">
                        Total {filteredWorkers.length} workers
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="relative" ref={sortRef}>
                        <button
                          onClick={() => setIsSortOpen((s) => !s)}
                          className="text-text-clr px-4 bg-[#ECE6F0] rounded-3xl py-1 font-semibold flex items-center"
                        >
                          Sort <ArrowDown className="w-4 h-4 inline-block ml-1" />
                        </button>
                        {isSortOpen && (
                          <div className="absolute right-0 mt-2 w-56 bg-white border rounded-xl shadow-lg z-20">
                            <div className="py-1 text-sm">
                              <button
                                className="w-full text-left px-4 py-2 hover:bg-gray-100"
                                onClick={() => {
                                  setSortBy("name_asc");
                                  setIsSortOpen(false);
                                }}
                              >
                                Name (A → Z)
                              </button>
                              <button
                                className="w-full text-left px-4 py-2 hover:bg-gray-100"
                                onClick={() => {
                                  setSortBy("name_desc");
                                  setIsSortOpen(false);
                                }}
                              >
                                Name (Z → A)
                              </button>
                              <div className="h-px bg-gray-200 my-1" />
                              <button
                                className="w-full text-left px-4 py-2 hover:bg-gray-100"
                                onClick={() => {
                                  setSortBy("attendance_desc");
                                  setIsSortOpen(false);
                                }}
                              >
                                Attendance % (High → Low)
                              </button>
                              <button
                                className="w-full text-left px-4 py-2 hover:bg-gray-100"
                                onClick={() => {
                                  setSortBy("attendance_asc");
                                  setIsSortOpen(false);
                                }}
                              >
                                Attendance % (Low → High)
                              </button>
                              <div className="h-px bg-gray-200 my-1" />
                              <button
                                className="w-full text-left px-4 py-2 hover:bg-gray-100"
                                onClick={() => {
                                  setSortBy("pay_desc");
                                  setIsSortOpen(false);
                                }}
                              >
                                Daily Wage (High → Low)
                              </button>
                              <button
                                className="w-full text-left px-4 py-2 hover:bg-gray-100"
                                onClick={() => {
                                  setSortBy("pay_asc");
                                  setIsSortOpen(false);
                                }}
                              >
                                Daily Wage (Low → High)
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          if (
                            userPermissions?.edit_worker_management ||
                            userPermissions?.is_admin ||
                            userPermissions?.joined_as === "owner"
                          ) {
                            setIsPaymentOpen(true);
                          } else {
                            addAlert(
                              "You must have edit worker management permission to add worker payments.",
                              "error"
                            );
                          }
                        }}
                        className="px-3 py-2 bg-[#3B0069] text-white rounded-xl font-medium flex items-center gap-2"
                      >
                        <span className="flex items-center justify-center rounded-full p-1 bg-[#58267F]">
                          <Plus className="w-4 h-4" />
                        </span>
                        Transaction
                      </button>
                      <button
                        onClick={() => {
                          if (
                            userPermissions?.edit_worker_management ||
                            userPermissions?.is_admin ||
                            userPermissions?.joined_as === "owner"
                          ) {
                            setIsNewWorkerOpen(true);
                          } else {
                            addAlert(
                              "You must have edit worker management permission to add new workers.",
                              "error"
                            );
                          }
                        }}
                        className="px-3 py-2 bg-[#3B0069] text-white rounded-xl font-medium flex items-center gap-2"
                      >
                        <div className="w-6 h-6 rounded-full bg-[#5E2D91] flex items-center justify-center">
                          <MdOutlineGroupAdd />
                        </div>
                        Worker
                      </button>
                    </div>
                  </div>

                  {/* Workers list */}
                  <div className="space-y-2">
                    {filteredWorkers.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        No attendance records found for the selected period
                      </div>
                    ) : (
                      sortedWorkers.map((w) => {
                        const dailyRate = workerRates[w.worker_id];
                        const presentDays = w.presentDays || 0;
                        const absentDays = w.absentDays || 0;
                        const totalDaysInRange = getDaysInRange();
                        const attendancePercent = Math.round(
                          (presentDays / totalDaysInRange) * 100
                        );
                        const totalPay =
                          dailyRate != null
                            ? `₹ ${(dailyRate * presentDays).toLocaleString(
                                "en-IN"
                              )}`
                            : "—";

                        const isPresent =
                          w.attendance_status?.toLowerCase() === "present";
                        const statusColor = isPresent
                          ? "bg-[#13C21A33] text-[#006604]"
                          : "bg-red-100 text-red-700";
                        const statusText = isPresent ? "Present" : "Absent";
                        const showDropdown = dateRange === "Daily" && isToday;

                        return (
                          <div
                            key={w.worker_id}
                            className="grid items-center gap-4 py-3 px-4 cursor-pointer bg-white rounded-xl shadow-sm md:grid-cols-4"
                            onClick={() =>
                              navigate(`/site/${siteId}/workers/${w.worker_id}`)
                            }
                            style={{
                              transition: "transform 0.2s",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "scale(1.01)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "scale(1)";
                            }}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                                <img src={profileIcon} alt="workerprofile" />
                              </div>
                              <div>
                                <p className="text-[16px] font-medium text-[#232323]">
                                  {w.worker_name}
                                </p>
                                <p className="text-sm text-[#232323]">
                                  {w.worker_workname || "—"}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Last updated by: {w.last_updated_by || "—"}
                                </p>
                              </div>
                            </div>

                            <div className="hidden md:block text-sm">
                              <div className="text-[14px] text-gray-500">
                                Attendance
                              </div>
                              <div className="text-[16px] font-medium text-[#232323]">
                                {attendancePercent}%
                              </div>
                            </div>
                            <div className="hidden md:block text-sm">
                              <div className="text-[14px] text-gray-500">
                                Daily Wage
                              </div>
                              <div className="text-[16px] font-medium text-[#232323]">
                                {totalPay}
                              </div>
                            </div>

                            <div className="flex items-center justify-end pr-10 gap-3">
                              {dateRange === "Daily" ? (
                                <span
                                  className={`px-4 py-1 rounded-lg text-sm flex items-center gap-1 ${statusColor} ${
                                    showDropdown
                                      ? "cursor-pointer hover:opacity-80"
                                      : ""
                                  }`}
                                  onClick={(e) =>
                                    showDropdown && handleAttendanceClick(e, w)
                                  }
                                >
                                  {statusText}
                                  {showDropdown && (
                                    <ChevronDown className="w-4 h-4" />
                                  )}
                                </span>
                              ) : (
                                <div className="text-sm">
                                  <span className="text-green-600 font-medium">
                                    {presentDays} Present
                                  </span>
                                  <span className="text-gray-400 mx-1">•</span>
                                  <span className="text-red-600 font-medium">
                                    {absentDays} Absent
                                  </span>
                                </div>
                              )}
                              <div className="flex flex-col items-center p-1 rounded-full hover:bg-gray-100">
                                <ArrowRightToLine className="w-5 h-5 text-[#232323]" />
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal for Attendance */}
      <WorkerAttendance
        isOpen={isAttendanceOpen}
        onClose={() => {
          setIsAttendanceOpen(false);
          fetchAllAttendance();
        }}
        workers={filteredWorkers}
        onSubmit={() => {
          setIsAttendanceOpen(false);
          fetchAllAttendance();
        }}
      />

      {/* Modal for Add New Worker */}
      {isNewWorkerOpen && (
        <UnifiedForm
          type="worker"
          onClose={() => {
            setIsNewWorkerOpen(false);
          }}
        />
      )}

      {/* Quick Mark Attendance Modal */}
      <QuickMarkAttendanceModal
        isOpen={quickMarkModal.open}
        onClose={() => setQuickMarkModal({ open: false, worker: null, attendanceStatus: null})}
        worker={quickMarkModal.worker}
        attendanceStatus={quickMarkModal.attendanceStatus}
        initialOvertimeHours={quickMarkModal.worker?.records[0]?.overtime_hours || 0}
        initialOvertimeWages={quickMarkModal.worker?.records[0]?.overtime_wage || 0}
        dayTypes={quickMarkModal.worker?.records[0]?.half_day ? "half" : quickMarkModal.worker?.records[0]?.full_day ? "full" : null}
        onUpdate={() => {
          fetchAllAttendance();
          setQuickMarkModal({ open: false, worker: null, attendanceStatus: null});
        }}
      />
    </div>
  );
};

export default WorkerDashboard;
